// Specification from https://docs.google.com/document/d/1XPRoEFDvRXeG8MCV3Pr3NPrwAaAL3swVzjhAp5os_Sc/edit?tab=t.0
import { FC, useMemo, useRef } from 'react'

import { HPVScreener } from '@hhgtech/hhg-components'
import { LOCALE_HAS_COMMUNITY_SITE } from '@hhgtech/hhg-components/constants'
import { VideoItemType } from '@hhgtech/hhg-components/types'
import { clsx, rem, Stack } from '@mantine/core'
import dynamic from 'next/dynamic'

import { HomeBanner } from 'components/atoms/HomeBanner'
import { HomeBannerFooter } from 'components/atoms/HomeBanner/HomeBannerFooter'
import {
  HomeCategory,
  HomeCategoryNewVersion,
} from 'components/atoms/HomeCategory'
import {
  HomeCommunity,
  HomeCommunityData,
} from 'components/atoms/HomeCommunity'
import { HomeExpert } from 'components/atoms/HomeExpert'
import { HomeFeature } from 'components/atoms/HomeFeature'
import { HomeHealthTool } from 'components/atoms/HomeHealthTool'
import { HomeSpotlight } from 'components/atoms/HomeSpotlight'
import { HomeTrendingLastestTopics } from 'components/atoms/HomeTrendingLastestTopics'
import { HomeTrustful } from 'components/atoms/HomeTrustful'
import { AdBlockContainer } from 'components/molecules/adBlock/adBlockContainer'
import { HomeVideos } from 'components/molecules/homeVideos'
import MobileBottomNavigationWrapper from 'components/organisms/mobileBottomNavigationWrapper'
import { useIsMobile } from 'hooks/useIsMobile'
import { useMastheadAdEnable } from 'hooks/useMastheadAdEnable'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { basePath, HELLO_BACSI_HPV_SCREENER } from 'interfaces/constants'
import { HEALTH_TOOL_NAME } from 'interfaces/constants/healthTools'
import { MARKET_HAS_VACCINATION } from 'interfaces/constants/vaccination'
import {
  ArticleApi,
  Category,
  CommunityItem,
  ExpertHeading,
  ExpertUserSummaryApi,
  HealthTool,
  HomepageBannerType,
  LOCALE,
  MedicalReviewApi,
  SpotlightSummary,
} from 'interfaces/types'
import { getAdTag, getCountryAdTag } from 'utils/ads'
import { handleDeepLink } from 'utils/handleDeepLink'

import { useHomeSpotlights } from './HomePage.hooks'
import { StyledHomeDivider } from './HomePage.styled'
import { StyledHomeWrapper } from './index.styled'
import { SectionWrapper } from './SectionWrapper'
import { InterstitialAd } from '../article/components/interstitialAd'
import { MastheadAd } from '../article/components/MastheadAd'

type FeaturedTopicApi = {
  posts: ArticleApi[]
  category: {
    term_id: number
    name: string
    permalink: string
    text_color: string
  }
}

export type { HomeCommunityData }

export type HomePageProps = {
  isMobileSsr?: boolean
  trendingArticles: ArticleApi[]
  categories: Category[]
  healthTools: HealthTool[]
  healthyLivingArticles: FeaturedTopicApi
  pregnancyArticles: FeaturedTopicApi
  drugArticles: FeaturedTopicApi
  expertsArticles: ExpertUserSummaryApi[]
  medicalReviewed: MedicalReviewApi[]
  spotlights: SpotlightSummary[]
  expertHeading: ExpertHeading
  banner?: HomepageBannerType
  footerBanner?: HomepageBannerType['footer_banner']
  homeCommunityData?: HomeCommunityData
  homeCommunities?: CommunityItem[]
  homeVideos?: VideoItemType[]
  newsEventsTitle: string
  homeHealthTool: HEALTH_TOOL_NAME
  homeHasMostReadPosts: boolean
  homeCategories: Category[]
}

const AdBlock = dynamic(
  () => import('@hhgtech/hhg-components').then((mod) => mod.AdBlock),
  {
    ssr: false,
  },
)

const FixedBottomAdBlock = dynamic(
  () => import('@hhgtech/hhg-components').then((mod) => mod.FixedBottomAdBlock),
  {
    ssr: false,
  },
)

const OutOfPageAd = dynamic(
  () =>
    import('../article/components/OutOfPageAd').then((mod) => mod.OutOfPageAd),
  {
    ssr: false,
  },
)

const LOCALE_SHOW_VIDEO = [LOCALE.Vietnam, LOCALE.Taiwan]

export const HomePage: FC<HomePageProps> = (props) => {
  const {
    banner,
    trendingArticles,
    healthTools,
    healthyLivingArticles,
    drugArticles,
    pregnancyArticles,
    expertsArticles,
    expertHeading,
    spotlights: spotlightsProps,
    medicalReviewed,
    footerBanner,
    homeCommunityData,
    homeCommunities,
    homeVideos,
    newsEventsTitle,
    homeHealthTool,
    homeHasMostReadPosts,
    homeCategories,
  } = props
  const isMobile = useIsMobile()
  const { locale } = useRouterExtend()

  const mastheadAdEnable = useMastheadAdEnable(banner?.adsOnArticles || {})

  const showAsideNavigatorEl = useRef<HTMLDivElement | null>(null)

  const { spotlights } = useHomeSpotlights({ spotlights: spotlightsProps })

  const isLocaleShowAd = locale === 'my-MM' || locale === 'km-KH'
  const isAltAdLayout = locale === 'th-TH' || locale === 'id-ID'

  const adUnitPath = getAdTag({
    countryAdTag: getCountryAdTag(locale, { isMobile }),
    contentAdTag: { result: 'HomePage', redundant: true },
  })

  const hasInterstitialAd =
    locale !== LOCALE.Cambodia &&
    locale !== LOCALE.Myanmar &&
    locale !== LOCALE.Vietnam
  const hasVaccination = MARKET_HAS_VACCINATION.includes(locale)

  const homeFeatureArticles = useMemo(() => {
    return [healthyLivingArticles, drugArticles, pregnancyArticles].filter(
      ({ posts }) => !!posts?.length,
    )
  }, [healthyLivingArticles, drugArticles, pregnancyArticles])

  return (
    <>
      <StyledHomeWrapper>
        <AdBlockContainer>
          {hasInterstitialAd && <InterstitialAd adUnitPath={adUnitPath} />}
          <MastheadAd
            adUnitPath={adUnitPath}
            className={'home-masthead'}
            hasPlaceholder={mastheadAdEnable}
          />
        </AdBlockContainer>
        {/* Hide SiteWide onScroll */}
        <div ref={showAsideNavigatorEl} />

        <Stack
          sx={(theme) => ({
            gap: 12,
            [theme.fn.largerThan('sm')]: {
              gap: 40,
            },
          })}
        >
          <Stack
            sx={(theme) => ({
              gap: 12,
              paddingTop:
                locale === LOCALE.Vietnam || locale === LOCALE.Malaysia
                  ? 0
                  : 12,
              [theme.fn.largerThan('sm')]: {
                gap: 24,
              },
            })}
          >
            {/* Carousel Category */}
            <SectionWrapper visible={hasVaccination}>
              {locale === LOCALE.Vietnam || locale === LOCALE.Malaysia ? (
                <HomeCategoryNewVersion className="block md:!p-0" />
              ) : (
                <HomeCategory
                  categories={homeCategories}
                  className="hidden md:block"
                />
              )}
            </SectionWrapper>
            <SectionWrapper visible={!hasVaccination}>
              <HomeCategory categories={homeCategories} />
            </SectionWrapper>
            <SectionWrapper visible={isMobile && locale === LOCALE.Vietnam}>
              <HPVScreener
                className="!px-4"
                onClick={() => handleDeepLink(HELLO_BACSI_HPV_SCREENER)}
                image={`${basePath}/images/hpv-screener.png`}
              />
            </SectionWrapper>
          </Stack>
          {/* Banner */}
          <HomeBanner
            isMobile={isMobile}
            homeBanner={banner}
            container={banner?.type === 'banner' ? SectionWrapper : undefined}
            containerProps={{ visible: true }}
          />
          {/* Trending/Lastest Topics */}
          <SectionWrapper
            visible={[...trendingArticles, ...newsEventsTitle].length > 0}
          >
            <HomeTrendingLastestTopics
              trendingArticles={trendingArticles}
              newsEventsTitle={newsEventsTitle}
              homeHasMostReadPosts={homeHasMostReadPosts}
            />
          </SectionWrapper>
          {/* Spotlight */}
          <SectionWrapper visible={!!spotlights.length}>
            <HomeSpotlight spotlights={spotlights} />
          </SectionWrapper>
          {/* Videos */}
          <SectionWrapper
            visible={LOCALE_SHOW_VIDEO.includes(locale) && !!homeVideos?.length}
          >
            <HomeVideos videos={homeVideos} />
          </SectionWrapper>
          {/* Community */}
          <SectionWrapper
            visible={
              LOCALE_HAS_COMMUNITY_SITE.includes(locale) &&
              !!homeCommunities?.length
            }
          >
            <HomeCommunity
              isMobile={isMobile}
              data={homeCommunityData}
              communities={homeCommunities}
              className={clsx({
                'md:!pt-10': !expertsArticles?.length,
              })}
            />
          </SectionWrapper>

          {isAltAdLayout && (
            <AdBlockContainer>
              <AdBlock
                adUnitPath={adUnitPath}
                size={[300, 250]}
                divId={'div-gpt-ad-home-mid-mobile-alt'}
                pos={['MidBanner1']}
                style={{ paddingBlock: rem(12), maxWidth: '100%' }}
              />
              <StyledHomeDivider style={{ borderBottom: 'none' }} />
            </AdBlockContainer>
          )}

          {/* Health tools */}
          <SectionWrapper visible={!!healthTools.length}>
            <HomeHealthTool
              healthTools={healthTools}
              embedCard={homeHealthTool}
            />
          </SectionWrapper>

          {/* Articles of category */}
          <SectionWrapper visible={!!homeFeatureArticles.length}>
            <HomeFeature
              items={homeFeatureArticles}
              sideComponent={
                <>
                  <AdBlock
                    adUnitPath={adUnitPath}
                    size={[300, 250]}
                    divId={'div-gpt-ad-home-sidebar1'}
                    pos={['HomeSidebar1']}
                    style={{ maxWidth: '100%' }}
                  />
                  {!isMobile && isLocaleShowAd && (
                    <AdBlock
                      adUnitPath={adUnitPath}
                      size={[300, 250]}
                      divId={'div-gpt-ad-home-sidebar2'}
                      pos={['HomeSidebar2']}
                      style={{ maxWidth: '100%', marginTop: rem(12) }}
                    />
                  )}
                </>
              }
            />
          </SectionWrapper>
          {/* Experts */}
          <SectionWrapper visible={!!expertsArticles.length}>
            <HomeExpert
              data={{ title: expertHeading?.title || '' }}
              experts={expertsArticles}
            />
          </SectionWrapper>

          {/* Footer Banner */}
          <SectionWrapper visible={!!footerBanner}>
            {footerBanner && (
              <HomeBannerFooter isMobile={isMobile} banner={footerBanner} />
            )}
          </SectionWrapper>

          {/* Trustful */}
          <SectionWrapper visible={!!medicalReviewed.length}>
            <HomeTrustful medicalReviewed={medicalReviewed} />
          </SectionWrapper>
        </Stack>

        <AdBlockContainer>
          {(isLocaleShowAd || isAltAdLayout) && (
            <FixedBottomAdBlock
              divId={'div-gpt-ad-category-sticky'}
              adUnitPath={adUnitPath}
              isMobile={isMobile}
              className="article-bottom-ad"
            />
          )}
        </AdBlockContainer>

        {/* Always show on mobile  */}
        {isMobile && <MobileBottomNavigationWrapper />}
      </StyledHomeWrapper>
      <OutOfPageAd id={`div-gpt-out-of-page`} adUnitPath={adUnitPath} />
    </>
  )
}
